import { store } from '../redux/store'
import { resetState, updateTokens } from '../redux/slices/auth.slice'
import { authAPI } from '../services/auth.service'

// Helper to decode JWT token payload
export const decodeJWTPayload = (token: string) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

// Check if token is expired (with 30 second buffer)
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWTPayload(token)
  if (!payload || !payload.exp) return true
  
  const currentTime = Math.floor(Date.now() / 1000)
  const bufferTime = 30 // 30 seconds buffer
  return payload.exp <= (currentTime + bufferTime)
}

// Store user data in localStorage with proper structure
export const saveUserToStorage = (userData: any) => {
  const userToStore = {
    firstName: userData.firstName || userData.name,
    name: userData.firstName || userData.name,
    email: userData.email,
    role: userData.role,
    access_token: userData.access_token,
    refresh_token: userData.refresh_token,
    tokenExpiry: userData.tokenExpiry
  }
  localStorage.setItem('user', JSON.stringify(userToStore))
}

// Automatically refresh token if needed
export const ensureValidToken = async (): Promise<boolean> => {
  const state = store.getState()
  const { access_token, refresh_token } = state.authReducer

  if (!access_token) return false

  // If token is not expired, we're good
  if (!isTokenExpired(access_token)) return true

  // If we have a refresh token, try to refresh
  if (refresh_token && !isTokenExpired(refresh_token)) {
    try {
      const result = await (store.dispatch as any)(
        authAPI.endpoints.refreshToken.initiate({ refresh_token })
      ).unwrap()

      if (result?.access_token) {
        const tokenExpiry = decodeJWTPayload(result.access_token)?.exp
        
        store.dispatch(updateTokens({
          access_token: result.access_token,
          refresh_token: result.refresh_token || refresh_token,
          tokenExpiry: tokenExpiry ? tokenExpiry * 1000 : undefined
        }))

        // Update localStorage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
        const updatedUser = {
          ...currentUser,
          access_token: result.access_token,
          refresh_token: result.refresh_token || refresh_token,
          tokenExpiry: tokenExpiry ? tokenExpiry * 1000 : undefined
        }
        localStorage.setItem('user', JSON.stringify(updatedUser))

        return true
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
    }
  }

  // If we get here, tokens are invalid - logout user
  store.dispatch(resetState())
  return false
}

// Initialize authentication from localStorage
export const initializeAuthFromStorage = () => {
  const userString = localStorage.getItem('user')
  if (!userString) return null

  try {
    const user = JSON.parse(userString)
    
    // Validate token structure
    if (!user.access_token) {
      localStorage.removeItem('user')
      return null
    }

    // Check if token is expired
    if (isTokenExpired(user.access_token)) {
      // If refresh token exists and is valid, we'll try to refresh later
      if (!user.refresh_token || isTokenExpired(user.refresh_token)) {
        localStorage.removeItem('user')
        return null
      }
    }

    return {
      name: user.firstName || user.name || '',
      email: user.email || '',
      access_token: user.access_token,
      refresh_token: user.refresh_token || '',
      role: user.role || '',
      tokenExpiry: user.tokenExpiry || null
    }
  } catch (error) {
    console.error('Error parsing user from localStorage:', error)
    localStorage.removeItem('user')
    return null
  }
}